import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import {
  Clock, Users, Activity, Target, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2, Search, FileDown,
} from "lucide-react";

export const fmtTime = (secs) => {
  if (!secs) return "—";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m`;
  return `${Math.round(secs)}s`;
};

const KPI = ({ icon: Icon, label, value, sub, testId }) => (
  <Card className="p-4 border-slate-200" data-testid={testId}>
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-[#E53935]/10 flex items-center justify-center shrink-0">
        <Icon size={17} className="text-[#E53935]" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="text-xl font-bold text-slate-900 leading-tight">{value}</div>
        {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
      </div>
    </div>
  </Card>
);

const PassRate = ({ value }) => {
  if (value == null) return <span className="text-slate-400">—</span>;
  const color = value >= 80 ? "text-emerald-600" : value >= 50 ? "text-amber-600" : "text-red-600";
  return <span className={`font-semibold ${color}`}>{value}%</span>;
};

export const AdminAnalytics = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState({});
  const [audience, setAudience] = useState("all"); // all | employees | dealers
  const [downloading, setDownloading] = useState(null); // "sales" | "lms" | null

  const downloadReport = async (kind) => {
    setDownloading(kind);
    const path = kind === "lms" ? "/admin/sales-report/lms-overview-pdf" : "/admin/sales-report/pdf";
    const name = kind === "lms" ? "UniVicoustic-LMS-Report" : "UniVicoustic-Sales-Report";
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't generate the report. Please retry.");
    } finally {
      setDownloading(null);
    }
  };

  useEffect(() => {
    setData(null);
    api.get("/admin/analytics", { params: { audience } })
      .then(({ data }) => setData(data))
      .catch(() => setError(true));
  }, [audience]);

  const AudienceToggle = (
    <div className="inline-flex p-1 bg-white border border-slate-200 rounded-lg" data-testid="analytics-audience-toggle">
      {[["all", "All"], ["employees", "Employees"], ["dealers", "Dealers"]].map(([val, label]) => (
        <button
          key={val}
          data-testid={`analytics-audience-${val}`}
          onClick={() => setAudience(val)}
          className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
            audience === val ? "bg-[#222222] text-white" : "text-slate-500 hover:text-slate-800"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  if (error) return <Card className="p-10 text-center text-sm text-slate-500 border-slate-200">Couldn't load analytics.</Card>;
  if (!data) return (
    <div>
      <div className="mb-4">{AudienceToggle}</div>
      <Card className="p-12 text-center border-slate-200" data-testid="analytics-loading">
        <div className="w-7 h-7 mx-auto border-2 border-[#E53935] border-t-transparent rounded-full animate-spin" />
      </Card>
    </div>
  );

  const { kpis, learners, module_stats, daily_activity, at_risk } = data;
  const filtered = learners.filter((l) =>
    l.learner_name.toLowerCase().includes(query.toLowerCase()) ||
    (l.department || "").toLowerCase().includes(query.toLowerCase()));
  const chart = daily_activity.map((d) => ({
    ...d,
    label: new Date(d.date + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric" }),
  }));

  return (
    <div className="space-y-8" data-testid="admin-analytics">
      {/* Audience segment */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {AudienceToggle}
          <button
            data-testid="lms-report-download"
            onClick={() => downloadReport("lms")}
            disabled={!!downloading}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#222222] hover:bg-black disabled:opacity-60 text-white text-xs font-semibold rounded-lg transition-colors"
          >
            <FileDown size={13} /> {downloading === "lms" ? "Generating…" : "LMS Overview Report (PDF)"}
          </button>
          <button
            data-testid="sales-report-download"
            onClick={() => downloadReport("sales")}
            disabled={!!downloading}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#E53935] hover:bg-[#c62f2c] disabled:opacity-60 text-white text-xs font-semibold rounded-lg transition-colors"
          >
            <FileDown size={13} /> {downloading === "sales" ? "Generating…" : "Sales Team Report (PDF)"}
          </button>
        </div>
        <span className="text-[11px] text-slate-400">
          {audience === "dealers" ? "Dealer accounts only" : audience === "employees" ? "Employees only" : "All learners"}
        </span>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI icon={Clock} label="Total learning time" value={fmtTime(kpis.total_learning_seconds)} sub="tracked since Jul 2026" testId="analytics-kpi-total-time" />
        <KPI icon={Activity} label="Active learners (7d)" value={kpis.active_learners_7d} testId="analytics-kpi-active-7d" />
        <KPI icon={Users} label="Learners with activity" value={kpis.tracked_learners} testId="analytics-kpi-tracked" />
        <KPI icon={Target} label="Overall quiz pass rate" value={kpis.overall_pass_rate != null ? `${kpis.overall_pass_rate}%` : "—"} sub="of learners who attempted" testId="analytics-kpi-pass-rate" />
      </div>

      {/* Daily activity */}
      <Card className="p-5 border-slate-200" data-testid="analytics-daily-chart">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-semibold text-slate-900">Daily activity</h3>
          <span className="text-[11px] text-slate-400">Last 30 days · reading minutes vs quiz attempts</span>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={chart} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={4} />
            <YAxis yAxisId="l" allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="r" orientation="right" allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="l" dataKey="minutes" name="Reading minutes" fill="#E53935" radius={[3, 3, 0, 0]} fillOpacity={0.85} />
            <Line yAxisId="r" type="monotone" dataKey="quiz_attempts" name="Quiz attempts" stroke="#222222" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>

      {/* Time by learner */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <h3 className="font-semibold text-slate-900">Time spent by learner</h3>
            <p className="text-[11px] text-slate-400">{data.time_tracking_note}</p>
          </div>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="analytics-learner-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search learner…"
              className="pl-8 h-8 w-56 text-sm"
            />
          </div>
        </div>
        <Card className="border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-2.5 font-semibold">Learner</th>
                <th className="px-4 py-2.5 font-semibold">Total time</th>
                <th className="px-4 py-2.5 font-semibold">Modules started</th>
                <th className="px-4 py-2.5 font-semibold">Quizzes passed</th>
                <th className="px-4 py-2.5 font-semibold">Avg score</th>
                <th className="px-4 py-2.5 font-semibold">Last active</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">No learners match.</td></tr>
              )}
              {filtered.map((l) => (
                <React.Fragment key={l.learner_id}>
                  <tr
                    data-testid={`analytics-learner-row-${l.learner_name}`}
                    onClick={() => setOpen((o) => ({ ...o, [l.learner_id]: !o[l.learner_id] }))}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                  >
                    <td className="px-4 py-2.5 text-sm">
                      <span className="inline-flex items-center gap-1.5 font-medium text-slate-900">
                        {open[l.learner_id] ? <ChevronDown size={13} className="text-slate-400"/> : <ChevronRight size={13} className="text-slate-400"/>}
                        {l.learner_name}
                      </span>
                      {l.department && <span className="ml-2 text-[10px] text-slate-400">{l.department}</span>}
                    </td>
                    <td className="px-4 py-2.5 text-sm font-mono font-medium text-slate-900" data-testid={`analytics-total-time-${l.learner_name}`}>{fmtTime(l.total_seconds)}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-600">{l.modules_started}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-600">{l.modules_passed}<span className="text-slate-400 text-xs"> · {l.quiz_attempts} attempts</span></td>
                    <td className="px-4 py-2.5 text-sm">{l.avg_score != null ? `${l.avg_score}%` : <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{l.last_active ? new Date(l.last_active).toLocaleDateString() : "—"}</td>
                  </tr>
                  {open[l.learner_id] && (
                    <tr className="bg-slate-50/60 border-b border-slate-100">
                      <td colSpan={6} className="px-4 py-3">
                        <div className="grid gap-1" data-testid={`analytics-learner-modules-${l.learner_name}`}>
                          {l.modules.length === 0 && <div className="text-xs text-slate-400 py-1">No reading activity recorded.</div>}
                          {l.modules.map((m) => (
                            <div key={m.module_id} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 items-center text-xs py-1.5 border-b border-slate-100 last:border-0">
                              <div className="min-w-0">
                                <span className="font-medium text-slate-800">{m.module_title}</span>
                                <span className="text-slate-400 ml-2">{m.course_title}</span>
                              </div>
                              <span className="font-mono font-medium text-slate-900 w-16 text-right">{fmtTime(m.seconds)}</span>
                              <span className="text-slate-500 w-20 text-right">read {m.scroll_pct}%</span>
                              <span className="w-20 text-right">
                                {m.quiz_passed
                                  ? <span className="inline-flex items-center gap-1 text-emerald-600 font-medium"><CheckCircle2 size={11}/> passed</span>
                                  : <span className="text-slate-400">no pass</span>}
                              </span>
                              <span className="text-slate-400 w-20 text-right">{m.last_viewed_at ? new Date(m.last_viewed_at).toLocaleDateString() : "—"}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      {/* Module performance */}
      <div>
        <h3 className="font-semibold text-slate-900 mb-3">Module performance</h3>
        <Card className="border-slate-200 overflow-x-auto">
          <table className="w-full min-w-[760px]" data-testid="analytics-module-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-2.5 font-semibold">Module</th>
                <th className="px-4 py-2.5 font-semibold">Readers</th>
                <th className="px-4 py-2.5 font-semibold">Avg time</th>
                <th className="px-4 py-2.5 font-semibold">Avg read depth</th>
                <th className="px-4 py-2.5 font-semibold">Attempts</th>
                <th className="px-4 py-2.5 font-semibold">Avg score</th>
                <th className="px-4 py-2.5 font-semibold">Pass rate</th>
              </tr>
            </thead>
            <tbody>
              {module_stats.map((m) => (
                <tr key={m.module_id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5 text-sm max-w-[280px]">
                    <div className="font-medium text-slate-900 truncate">{m.module_title}</div>
                    <div className="text-[10px] text-slate-400 truncate">{m.course_title}</div>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-600">{m.readers}</td>
                  <td className="px-4 py-2.5 text-sm font-mono text-slate-900">{fmtTime(m.avg_seconds)}</td>
                  <td className="px-4 py-2.5 text-sm text-slate-600">{m.avg_scroll}%</td>
                  <td className="px-4 py-2.5 text-sm text-slate-600">{m.has_quiz ? m.attempts : <span className="text-slate-400 text-xs">no quiz</span>}</td>
                  <td className="px-4 py-2.5 text-sm">{m.avg_score != null ? `${m.avg_score}%` : <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-2.5 text-sm"><PassRate value={m.pass_rate} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      {/* At-risk learners */}
      <div>
        <h3 className="font-semibold text-slate-900 mb-3 inline-flex items-center gap-2">
          <AlertTriangle size={15} className="text-amber-500" /> At-risk learners
          <span className="text-[11px] font-normal text-slate-400">no activity in 14+ days</span>
        </h3>
        {at_risk.length === 0 ? (
          <Card className="p-6 text-center text-sm text-slate-500 border-dashed border-slate-200" data-testid="analytics-at-risk-empty">
            Everyone with activity has been active in the last 14 days. 🎉
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="analytics-at-risk-list">
            {at_risk.map((r) => (
              <Card key={r.learner_name} className="p-4 border-amber-200 bg-amber-50/40">
                <div className="font-medium text-slate-900 text-sm">{r.learner_name}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {r.department || "—"} · last active {r.last_active ? new Date(r.last_active).toLocaleDateString() : "never"}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">{r.modules_started} started · {r.modules_passed} quizzes passed</div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
